import React, { useState, useEffect, useId } from 'react';

import { useTranslation } from 'react-i18next';

import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip";
// Names come from the registry at runtime, so the icon component has to be
// resolved by name instead of imported statically.
import { fluentIconComponent } from "@/components/fluent-icon-map";
import { cn } from "@/lib/utils";
import { icons as lucideIcons } from "lucide-react";

// The full lucide-react icon registry — gives the user the broad spectrum
// they expect. `fluentIconComponent` looks up by name with this same registry
// as a fallback after `FLUENT_ICON_MAP`, so picking any name here renders
// correctly elsewhere in the app. Names ending in "Icon" are deprecated
// aliases lucide ships for backwards compatibility — we hide them so the
// picker shows each icon only once.
export const FLUENT_ICONS = Object.keys(lucideIcons)
  .filter((n) => !n.endsWith("Icon"))
  .sort();

// Legacy hard-coded list, kept for reference. Names not in FLUENT_ICON_MAP
// won't render; either add them to the map or remove from this list.
const _LEGACY_FLUENT_ICONS_REFERENCE = [ // eslint-disable-line @typescript-eslint/no-unused-vars
    'Add', 'Accept', 'AccessibiltyChecker', 'AccountManagement', 'AddBookmark', 'AddEvent', 'AddFavorite',
    'AddFriend', 'AddGroup', 'AddIn', 'AddLink', 'AddNotes', 'AddOnlineMeeting', 'AddPhone', 'AddTo',
    'Admin', 'AdminALogo32', 'AdminCLogoInverse32', 'AdminDLogo32', 'AdminELogo32', 'AdminLLogo32',
    'AdminMLogo32', 'AdminOLogo32', 'AdminPLogo32', 'AdminSLogo32', 'AdminYLogo32', 'Airplane', 'AirplaneSolid',
    'AirTickets', 'AlarmClock', 'Album', 'AlbumRemove', 'AlertSolid', 'AlignCenter', 'AlignLeft', 'AlignRight',
    'AnalyticsLogo', 'AnalyticsQuery', 'AnalyticsReport', 'AnalyticsView', 'Anchor', 'AndroidLogo', 'Annotation',
    'Archive', 'AreaChart', 'ArrangeBringForward', 'ArrangeBringToFront', 'ArrangeSendBackward', 'ArrangeSendToBack',
    'ArrowDownRight8', 'ArrowDownRightMirrored8', 'ArrowTallDownLeft', 'ArrowTallDownRight', 'ArrowTallUpLeft',
    'ArrowTallUpRight', 'ArrowUpRight', 'ArrowUpRight8', 'ArrowUpRightMirrored8', 'Articles', 'Ascending',
    'Asterisk', 'AsteriskSolid', 'Attach', 'AustralianRules', 'AutoEnhanceOff', 'AutoEnhanceOn', 'AutoFillTemplate',
    'AutoRacing', 'AwayStatus', 'AzureAPIManagement', 'AzureDataExplorer', 'AzureIcon', 'AzureKeyVault',
    'AzureServiceEndpoint', 'Back', 'BackgroundColor', 'BarChart4', 'BarChartHorizontal', 'BarChartVertical',
    'Baseball', 'BidiLtr', 'BidiRtl', 'Blocked', 'Blocked2', 'BlockedSolid', 'BlowingSnow', 'Blur', 'Boards',
    'Bold', 'BookingsLogo', 'Bookmark', 'BookmarkOutline', 'BookmarkSolid', 'BorderDash', 'BorderDot',
    'BoxAdditionSolid', 'BoxCheckmarkSolid', 'BoxMultiplySolid', 'BoxPlaySolid', 'BoxSubtractSolid',
    'BranchCommit', 'BranchCompare', 'BranchFork', 'BranchFork2', 'BranchLocked', 'BranchMerge', 'BranchPullRequest',
    'BranchSearch', 'BranchShelveset', 'Breadcrumb', 'Breakfast', 'Brightness', 'Broom', 'Brush', 'BucketColor',
    'BucketColorFill', 'BufferTimeAfter', 'BufferTimeBefore', 'BufferTimeBoth', 'Bug', 'BugSolid', 'Build',
    'BuildIssue', 'BuildQueue', 'BuildQueueNew', 'BulkUpload', 'BulletedList', 'BulletedList2', 'BulletedList2Mirrored',
    'CheckList', 'CityNext', 'CityNext2', 'Cotton', 'Cycling', 'D365TalentLearn',
    'BulletedListMirrored', 'Bullseye', 'Bus', 'BusinessCenterLogo', 'BusinessHoursSign', 'BusSolid',
    'Cafe', 'Cake', 'Calculator', 'CalculatorAddition', 'CalculatorEqualTo', 'CalculatorMultiply', 'CalculatorNotEqualTo',
    'CalculatorSubtract', 'Calendar', 'CalendarAgenda', 'CalendarDay', 'CalendarMirrored', 'CalendarReply',
    'CalendarSettings', 'CalendarSettingsMirrored', 'CalendarWeek', 'CalendarWorkWeek', 'CaloriesAdd', 'Camera',
    'Cancel', 'CannedChat', 'Car', 'CaretBottomLeftCenter8', 'CaretBottomLeftSolid8', 'CaretBottomRightCenter8',
    'CaretBottomRightSolid8', 'CaretDown8', 'CaretDownSolid8', 'CaretHollow', 'CaretHollowMirrored', 'CaretLeft8',
    'CaretLeftSolid8', 'CaretRight', 'CaretRight8', 'CaretRightSolid8', 'CaretSolid', 'CaretSolid16', 'CaretSolidDown',
    'CaretSolidLeft', 'CaretSolidMirrored', 'CaretSolidRight', 'CaretSolidUp', 'CaretTopLeftCenter8', 'CaretTopLeftSolid8',
    'CaretTopRightCenter8', 'CaretTopRightSolid8', 'CaretUp8', 'CaretUpSolid8', 'Cat', 'CellPhone', 'Certificate',
    'CertifiedDatabase', 'Chart', 'ChartSeries', 'Chat', 'ChatInviteFriend', 'ChatSolid', 'Checkbox', 'CheckboxComposite',
    'CheckboxCompositeReversed', 'CheckboxIndeterminate', 'CheckboxIndeterminateCombo', 'CheckList', 'CheckMark',
    'ChevronDown', 'ChevronDownEnd6', 'ChevronDownMed', 'ChevronDownSmall', 'ChevronFold10', 'ChevronLeft',
    'ChevronLeftEnd6', 'ChevronLeftMed', 'ChevronLeftSmall', 'ChevronRight', 'ChevronRightEnd6', 'ChevronRightMed',
    'ChevronRightSmall', 'ChevronUnfold10', 'ChevronUp', 'ChevronUpEnd6', 'ChevronUpMed', 'ChevronUpSmall',
    'ChromeBack', 'ChromeBackMirrored', 'ChromeClose', 'ChromeMinimize', 'ChromeRestore', 'CircleAddition',
    'CircleAdditionSolid', 'CircleFill', 'CircleHalfFilled', 'CirclePause', 'CirclePauseSolid', 'CirclePlus',
    'CircleRing', 'CircleStop', 'CircleStopSolid', 'CityNext', 'CityNext2', 'ClassNotebookLogoInverse',
    'Clear', 'ClearFilter', 'ClearFormatting', 'ClearNight', 'ClipboardList', 'ClipboardListMirrored', 'Clock',
    'CloneToDesktop', 'ClosedCaption', 'Cloud', 'CloudAdd', 'CloudDownload', 'CloudUpload', 'CloudWeather',
    'Cloudy', 'Cocktails', 'Code', 'Coffee', 'CollabsDBLogo', 'CollapseMenu', 'CollegeFootball', 'CollegeHoops',
    'Color', 'ColorSolid', 'ColumnLeftTwoThirds', 'ColumnLeftTwoThirdsEdit', 'ColumnOptions', 'ColumnRightTwoThirds',
    'ColumnRightTwoThirdsEdit', 'Combine', 'Combobox', 'CommandPrompt', 'Comment', 'CommentAdd', 'CommentNext',
    'CommentPrevious', 'CommentUrgent', 'Communications', 'CompanyDirectory', 'CompanyDirectoryMirrored',
    'CompassNW', 'Completed', 'CompletedSolid', 'ConfigurationSolid', 'Connect', 'ConstructionCone', 'ConstructionConeSolid',
    'Contact', 'ContactCard', 'ContactHeart', 'ContactInfo', 'ContactLink', 'ContactLock', 'ContactPhone',
    'ContactSettings', 'Copy', 'Cotton', 'Cricket', 'CSS', 'CustomList', 'CustomListMirrored', 'Cut',
    'Cycling', 'DataConnectionLibrary', 'DataManagementSettings', 'DateTime', 'DateTime2', 'DateTimeMirrored',
    'DeactivateOrders', 'DecisionSolid', 'Delete', 'DeleteColumns', 'DeleteRows', 'DeleteRowsMirrored', 'DeleteTable',
    'DeliveryTruck', 'DelveAnalytics', 'DelveAnalyticsLogo', 'DelveLogo', 'DelveLogoInverse', 'DependencyAdd',
    'DependencyRemove', 'Deploy', 'Descending', 'Design', 'DesktopScreenshot', 'DeveloperTools', 'Devices3',
    'Devices4', 'Diagnostic', 'Dialpad', 'Dictionary', 'DietPlanNotebook', 'DisableUpdates', 'Dislike',
    'DockLeft', 'DockLeftMirrored', 'DockRight', 'DockRightMirrored', 'Document', 'Documentation', 'DocumentReply',
    'DocumentSearch', 'DocumentSet', 'Door', 'DoubleBookmark', 'DoubleChevronDown', 'DoubleChevronDown12',
    'DoubleChevronDown8', 'DoubleChevronLeft', 'DoubleChevronLeft12', 'DoubleChevronLeft8', 'DoubleChevronLeftMed',
    'DoubleChevronLeftMedMirrored', 'DoubleChevronRight', 'DoubleChevronRight12', 'DoubleChevronRight8',
    'DoubleChevronUp', 'DoubleChevronUp12', 'DoubleChevronUp8', 'DoubleColumn', 'DoubleColumnEdit', 'Down',
    'Download', 'DragObject', 'DrillDown', 'DrillDownSolid', 'DrillExpand', 'DrillShow', 'DrillThrough',
    'DRM', 'Drop', 'Dropdown', 'DropShape', 'DropShapeSolid', 'DuplicateRow', 'Duststorm', 'DynamicList',
    'EatDrink', 'EdgeLogo', 'Edit', 'EditContact', 'EditCreate', 'EditList', 'EditMail', 'EditMirrored',
    'EditNote', 'EditPhoto', 'EditSolid', 'EditStyle', 'Embed', 'EMI', 'Emoji', 'Emoji2', 'EmojiDisappointed',
    'EmojiNeutral', 'EmojiTabSymbols',     'EmptyRecycleBin', 'EngineeringGroup', 'EntryDecline', 'EntryView',
    'Equalizer', 'EraseTool', 'Error', 'ErrorBadge', 'Event', 'EventAccepted', 'EventDeclined', 'EventInfo',
    'IncreaseIndentLegacy', 'Lightbulb', 'MapPin', 'Money', 'Nav2DMap', 'Nav2DMapView', 'NetworkTower',
    'EventTentative', 'EventTentativeMirrored', 'ExcelDocument', 'ExcelLogo', 'ExcelLogoInverse', 'ExchangeLogo',
    'ExchangeLogoInverse', 'ExerciseTracker', 'ExpandMenu', 'ExploreContent', 'ExploreContentSingle',
    'Export', 'ExportMirrored', 'ExternalBuild', 'ExternalTFVC', 'ExternalXAML', 'F12DevTools', 'Family',
    'FangBody', 'FastForward', 'FastMode', 'Favicon', 'FavoriteList', 'FavoriteStar', 'FavoriteStarFill',
    'Fax', 'Ferry', 'FerrySolid', 'FieldChanged', 'FieldEmpty', 'FieldFilled', 'FieldNotChanged', 'FieldReadOnly',
    'FileASPX', 'FileBug', 'FileCode', 'FileComment', 'FileHTML', 'FileImage', 'FileJAVA', 'FileLess',
    'FilePDB', 'FileSass', 'FileSQL', 'FileSymlink', 'FileTemplate', 'FileTypeSolution', 'Filter', 'Filters',
    'FilterSolid', 'Financial', 'FinancialMirroredSolid', 'FinancialSolid', 'Fingerprint', 'FiveTileGrid',
    'FiveTileGridMirrored', 'Flag', 'FlickDown', 'FlickLeft', 'FlickRight', 'FlickUp', 'Flow', 'FocalPoint',
    'Focus', 'FocusView', 'Font', 'FontColor', 'FontDecrease', 'FontIncrease', 'FontSize', 'FormLibrary',
    'FormLibraryMirrored', 'Forward', 'ForwardEvent', 'Freezing', 'Frigid', 'FullScreen', 'FullWidth',
    'FullWidthEdit', 'FunctionalManagerDashboard', 'FunnelChart', 'Game', 'Generate', 'GenericScan',
    'Giftbox', 'GiftBoxSolid', 'GiftCard', 'GitGraph', 'Glasses', 'Glimmer', 'GlobalNavButton', 'Globe',
    'GlobeFavorite', 'Golf', 'GotoToday', 'GripperBarVertical', 'GripperTool', 'Group', 'GroupedAscending',
    'GroupedDescending', 'GroupedList', 'GroupObject', 'GUID', 'Guitar', 'HailDay', 'HailNight', 'HalfAlpha',
    'HalfCircle', 'HandsFree', 'Handwriting', 'HardDrive', 'HardDriveGroup', 'HardDriveLock', 'HardDriveUnlock',
    'Header1', 'Header2', 'Header3', 'Header4', 'Headset', 'HeadsetSolid', 'Health', 'HealthSolid', 'Heart',
    'HeartBroken', 'HeartFill', 'Help', 'HelpMirrored', 'Hexagon', 'Hide', 'Hide2', 'Hide3', 'Highlight',
    'HighlightMappedShapes', 'History', 'Home', 'HomeSolid', 'HorizontalDistributeCenter', 'Hospital',
    'Hotel', 'IconSetsFlag', 'IDBadge', 'ImageCrosshair', 'ImageDiff', 'ImagePixel', 'ImageSearch', 'Import',
    'ImportMirrored', 'Important', 'Inbox', 'InboxCheck', 'IncidentTriangle', 'IncreaseIndentLegacy',
    'IncreaseIndentText', 'IncreaseIndentTextMirrored', 'Info', 'Info2', 'InfoSolid', 'InsertColumnsLeft',
    'InsertColumnsRight', 'InsertRowsAbove', 'InsertRowsBelow', 'InsertSignatureLine', 'InsertTextBox',
    'InstallToDrive', 'InternetSharing', 'IRMForward', 'IRMForwardMirrored', 'IRMReply', 'IRMReplyMirrored',
    'IssueSolid', 'IssueTracking', 'IssueTrackingMirrored', 'Italic', 'JavaScript', 'JoinOnlineMeeting',
    'JS', 'Label', 'LadybugSolid', 'Lamp', 'LandscapeOrientation', 'LaptopSelected', 'LargeGrid', 'LeaveChat',
    'LeaveChatMirrored', 'Library', 'Lifesaver', 'LifesaverLock', 'Light', 'Lightbulb', 'LightningBolt',
    'Like', 'LikeSolid', 'LineChart', 'LineSpacing', 'LineStyle', 'LineThickness', 'Link', 'Link12', 'LinkedDatabase',
    'List', 'ListMirrored', 'LocaleLanguage', 'Location', 'LocationCircle', 'LocationDot', 'LocationFill',
    'LocationOutline', 'Lock', 'LockSolid', 'LogRemove', 'LookupEntities', 'LowerBrightness', 'LyncLogo',
    'Mail', 'MailAlert', 'MailCheck', 'MailFill', 'MailForward', 'MailForwardMirrored', 'MailLowImportance',
    'MailPause', 'MailReply', 'MailReplyAll', 'MailReplyAllMirrored', 'MailReplyMirrored', 'MailSchedule',
    'MailSolid', 'MailTentative', 'MailTentativeMirrored', 'MailUndelivered', 'ManagerSelfService', 'MapDirections',
    'MapPin', 'MapPinSolid', 'MarkDownLanguage', 'Market', 'MarketDown', 'Megaphone', 'MegaphoneSolid',
    'Memo', 'Merge', 'Message', 'MessageFill', 'Microphone', 'MicrosoftFlowLogo', 'MicrosoftStaffhubLogo',
    'MiniContract', 'MiniExpand', 'MiniLink', 'Mobile', 'MobileReport', 'Money', 'More', 'MoreSports',
    'MoreVertical', 'Move', 'Movers', 'MoveToFolder', 'MSNLogo', 'MSNVideos', 'MSNVideosSolid', 'MTMLogo',
    'MultiSelect', 'MultiSelectMirrored', 'MusicInCollection', 'MusicInCollectionFill', 'MusicNote', 'MyMoviesTV',
    'Nav2DMapView', 'NavigateBack', 'NavigateBackMirrored', 'NavigateExternalInline', 'NavigateForward',
    'NavigateForwardMirrored', 'NetworkTower', 'NewAnalyticsQuery', 'NewMail', 'News', 'NewsSearch', 'NewTeamProject',
    'Next', 'NonprofitLogo32', 'NoteForward', 'NotePinned', 'NoteReply', 'NotExecuted', 'NotImpactedSolid',
    'NugetLogo', 'NumberedList', 'NumberedListMirrored', 'NumberedListText', 'NumberedListTextMirrored',
    'NumberField', 'NumberSequence', 'OfficeAddinsLogo', 'OfficeAssistantLogo', 'OfficeChat', 'OfficeChatSolid',
    'OfficeFormsLogo', 'OfficeFormsLogoInverse', 'OfficeFormsLogoInverse16', 'OfficeFormsLogoInverse24',
    'OfficeLogo', 'OfficeStoreLogo', 'OfficeVideoLogo', 'OfficeVideoLogoFill', 'OfficeVideoLogoInverse',
    'OfficeVideoLogoInverse16', 'OfficeVideoLogoInverse24', 'OfflineOneDriveParachute', 'OfflineOneDriveParachuteDisabled',
    'OneDrive', 'OneDriveAdd', 'OneDriveFolder16', 'OneDriveLogo', 'OneNoteEduLogoInverse', 'OneNoteLogo',
    'OneNoteLogoInverse', 'OpenFileHorizontal', 'OpenInNewWindow', 'OpenPane', 'OpenPaneMirrored', 'OpenSource',
    'Org', 'OutlookLogo', 'OutlookLogoInverse', 'OutlookSpacesBucket', 'OutlookSpacesBucketInverse', 'OutlookSpacesMenu',
    'OutlookSpacesMenuInverse', 'OutOfOffice', 'Package', 'Packages', 'Page', 'PageAdd', 'PageArrowRight',
    'PageCheckedin', 'PageCheckedOut', 'PageEdit', 'PageLeft', 'PageListFilter', 'PageListGroup', 'PageListPinned',
    'PageListSolid', 'PageRemove', 'PageRight', 'PageSolid', 'PanoIndicator', 'ParatureLogo', 'PartlyClearNight',
    'PartlyCloudyDay', 'PartlyCloudyNight', 'PartyLeader', 'Paste', 'Pause', 'PaymentCard', 'PC1', 'PDF',
    'PencilReply', 'Pentagon', 'People', 'PeopleAdd', 'PeopleAlert', 'PeopleBlock', 'PeoplePause', 'PeopleRepeat',
    'Permissions', 'PermissionsSolid', 'Personalize', 'Phishing', 'Phone', 'Photo', 'Photo2', 'Photo2Add',
    'Photo2Remove', 'PhotoCollection', 'Picture', 'PictureCenter', 'PictureFill', 'PictureLibrary', 'PicturePosition',
    'PictureTile', 'PieDouble', 'PieSingle', 'Pinned', 'PinnedFill', 'PivotChart', 'PlannerLogo', 'PlanView',
    'Play', 'PlayerSettings', 'PlayResume', 'Plug', 'PlugConnected', 'PlugDisconnected', 'PlugSolid', 'POI',
    'POISolid', 'PostUpdate', 'PowerApps', 'PowerApps2Logo', 'PowerAppsLogo', 'PowerBILogo', 'PowerButton',
    'PowerPointDocument', 'PowerPointLogo', 'PowerPointLogoInverse', 'PowerShell2', 'Precipitation', 'PresenceChickletVideo',
    'Preview', 'PreviewLink', 'Previous', 'Print', 'PrintfaxPrinterFile', 'Processing', 'ProcessingCancel',
    'ProcessingRun', 'Product', 'ProductCatalog', 'ProductList', 'ProductRelease', 'ProductVariant', 'ProfileSearch',
    'ProFootball', 'ProHockey', 'Project', 'ProjectCollection', 'ProjectLogoInverse', 'PromotedDatabase',
    'ProtectedDocument', 'ProtectRestrict', 'PublicCalendar', 'PublicContactCard', 'PublicEmail', 'PublicFolder',
    'PublishCourse', 'PublisherLogo', 'PublisherLogoInverse', 'Puzzle', 'Questionnaire', 'QuestionnaireMirrored',
    'QuickNote', 'QuickNoteSolid', 'RadioBtnOff', 'RadioBtnOn', 'RadioBullet', 'Rain', 'RainShowersDay',
    'RainShowersNight', 'RainSnow', 'RawSource', 'Read', 'ReadingMode', 'ReceiptCheck', 'ReceiptForward',
    'ReceiptReply', 'ReceiptTentative', 'Recent', 'Record2', 'RecurringEvent', 'RecurringTask', 'RecycleBin',
    'Redeploy', 'RedEye', 'Redo', 'Refresh', 'ReminderGroup', 'ReminderPerson', 'Remote', 'Remove', 'RemoveEvent',
    'RemoveFilter', 'RemoveLink', 'RemoveOccurrence', 'Rename', 'ReopenPages', 'Repair', 'Reply', 'ReplyAll',
    'ReplyAllAlt', 'ReplyAllMirrored', 'ReplyAlt', 'ReplyMirrored', 'ReportLibrary', 'ReportLibraryMirrored',
    'ReturnToSession', 'RevToggleKey', 'Rewind', 'Ribbon', 'RibbonSolid', 'RightDoubleQuote', 'Ringer',
    'Robot', 'Rocket', 'Room', 'Rotate', 'Rotate90CounterClockwise', 'RotateClockwise', 'RowsChild', 'RowsGroup',
    'Rugby', 'Running', 'Sad', 'SadSolid', 'Save', 'SaveAll', 'SaveAs', 'ScheduleEventAction', 'ScopeTemplate',
    'Script', 'ScrollUpDown', 'Search', 'SearchAndApps', 'Section', 'Sections', 'SecurityGroup', 'Send',
    'SendMirrored', 'Separator', 'Server', 'ServerEnviroment', 'ServerProcesses', 'SetAction', 'Settings',
    'Share', 'ShareiOS', 'SharepointLogo', 'SharepointLogoInverse', 'Shield', 'ShieldSolid', 'Shop', 'ShoppingCart', 'Shopping',
    'ShowResults', 'ShowResultsMirrored', 'SidePanel', 'SidePanelMirrored', 'SignOut', 'SingleBookmark',
    'SingleColumn', 'SingleColumnEdit', 'SIPMove', 'SiteScan', 'SizeLegacy', 'SkiResorts', 'SkypeCheck',
    'SkypeCircleCheck', 'SkypeCircleClock', 'SkypeCircleMinus', 'SkypeClock', 'SkypeForBusinessLogo', 'SkypeForBusinessLogoFill',
    'SkypeForBusinessLogoInverse16', 'SkypeForBusinessLogoInverse24', 'SkypeLogo', 'SkypeMessage', 'SkypeMinus',
    'SkypeSlash', 'Slider', 'SliderHandleSize', 'SliderThumb', 'Snow', 'Snowflake', 'SnowShowerDay', 'SnowShowerNight',
    'Soccer', 'SocialListening', 'Sort', 'SortDown', 'SortLines', 'SortUp', 'Spacer', 'Speakers', 'SpeedHigh',
    'Split', 'Squalls', 'Stack', 'StackIndicator', 'StaffNotebookLogo', 'StaffNotebookLogoInverse', 'StaffNotebookLogoInverse16',
    'StaffNotebookLogoInverse24', 'Starburst', 'StarburstSolid', 'StatusErrorFull', 'StatusTriangle', 'Step',
    'StepInsert', 'StepShared', 'StepSharedAdd', 'StepSharedInsert', 'StockDown', 'StockUp', 'Stop', 'StopSolid',
    'Stopwatch', 'StoreLogo', 'StoreLogoMed', 'Storyboard', 'StreamLogo', 'Streaming', 'StreamingOff', 'Subscribe',
    'Subscript', 'SubstitutionsIn', 'Suitcase', 'SunAdd', 'Sunny', 'SunnySolid', 'Superscript', 'SurveyQuestions',
    'SwayLogoInverse', 'Switch', 'SwitcherStartEnd', 'Sync', 'SyncFolder', 'SyncOccurence', 'SyncToPC', 'System',
    'Tab', 'Table', 'Tablet', 'TabletMode', 'Tag', 'TaskManager', 'TaskManagerMirrored', 'TeamLogo', 'TeamsLogo',
    'TeamsLogoInverse', 'Teamwork', 'TemporaryUser', 'Tennis', 'TextBox', 'TextCallout', 'TextDocument',
    'TextField', 'TextOverflow', 'ThisPC', 'ThreeQuarterCircle', 'ThumbnailView', 'ThumbnailViewMirrored',
    'Thunderstorms', 'TimeEntry', 'Timeline', 'Timer', 'ToDoLogoBottom', 'ToDoLogoInverse', 'ToDoLogoOutline',
    'ToDoLogoTop', 'ToggleBorder', 'ToggleFilled', 'ToggleThumb', 'Touch', 'TouchPointer', 'Train', 'TrainSolid',
    'TransferCall', 'Transition', 'Trending12', 'TrendingDown', 'TrendingUp', 'TriangleDown12', 'TriangleLeft12',
    'TriangleRight12', 'TriangleSolid', 'TriangleSolidDown12', 'TriangleSolidLeft12', 'TriangleSolidRight12',
    'TriangleSolidUp12', 'TriangleUp12', 'TriggerApproval', 'TriggerAuto', 'TriggerFiring', 'TriggerPhrase',
    'TriggerUser', 'Trim', 'TrimEnd', 'TrimStart', 'TripleColumn', 'TripleColumnEdit', 'TripleColumnWide',
    'TurnRight', 'TVMonitor', 'TVMonitorSelected', 'TypeScriptLanguage', 'Umbrella', 'Underline', 'Undo',
    'Uneditable', 'Uneditable2', 'Uneditable2Mirrored', 'UneditableMirrored', 'UneditableSolid12', 'UneditableSolidMirrored12',
    'Unfavorite', 'UngroupObject', 'Unknown', 'UnknownCall', 'Unlock', 'UnlockSolid', 'Unpin', 'Unsubscribe',
    'UnsyncFolder', 'UnsyncOccurence', 'Up', 'Upload', 'UserEvent', 'UserGauge', 'UserOptional', 'UserPause',
    'UserRemove', 'UserSync', 'Vacation', 'Vendor', 'VerifiedBrand', 'Video', 'VideoOff', 'VideoSearch',
    'VideoSolid', 'View', 'ViewAll', 'ViewAll2', 'ViewDashboard', 'ViewInAR', 'ViewList', 'ViewListGroup',
    'ViewListTree', 'VisioDocument', 'VisioLogo', 'VisioLogoInverse', 'VisualBasicLanguage', 'VisualStudioLogo',
    'VoicemailForward', 'VoicemailReply', 'Volume0', 'Volume1', 'Volume2', 'Volume3', 'Waffle', 'WaffleOffice365',
    'WaitlistConfirm', 'WaitlistConfirmMirrored', 'Warning', 'Warning12', 'Website', 'Weights', 'WifiEthernet',
    'WindDirection', 'WindowsLogo', 'WipePhone', 'WordDocument', 'WordLogo', 'WordLogoInverse', 'Work',
    'WorkFlow', 'WorkItem', 'WorkItemBug', 'WorkItemQuery', 'World', 'WorldClock', 'YammerLogo', 'Zoom',
    'ZoomIn', 'ZoomOut', 'ZoomToFit', 'DocumentMultiple'
];

interface FluentIconSelectorProps {
    selectedIcon?: string;
    onIconChange?: (iconName: string) => void;
    iconColor?: string;
    label?: string;
    placeholder?: string;
    disabled?: boolean;
}

export const FluentIconSelector: React.FC<FluentIconSelectorProps> = ({
    selectedIcon = 'CityNext',
    onIconChange,
    iconColor = '#0078d4',
    label = 'Icon',
    placeholder = 'Select an icon...',
    disabled = false
}) => {
    const { t } = useTranslation();
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [filteredIcons, setFilteredIcons] = useState(FLUENT_ICONS);
    const fieldId = useId();
    const triggerId = `${fieldId}-trigger`;
    const searchId = `${fieldId}-search`;

    useEffect(() => {
        if (searchTerm.trim() === '') {
            setFilteredIcons(FLUENT_ICONS);
        } else {
            const filtered = FLUENT_ICONS.filter(icon =>
                icon.toLowerCase().includes(searchTerm.toLowerCase())
            );
            setFilteredIcons(filtered);
        }
    }, [searchTerm]);

    const handleIconSelect = (iconName: string) => {
        if (onIconChange) {
            onIconChange(iconName);
        }
        setIsDialogOpen(false);
        setSearchTerm('');
    };

    const SelectedIconComponent = fluentIconComponent(selectedIcon);

    return (
        <>
            <div className="flex flex-col items-center gap-1">
                {label && <Label htmlFor={triggerId}>{label}</Label>}
                <button
                    type="button"
                    id={triggerId}
                    disabled={disabled}
                    onClick={() => setIsDialogOpen(true)}
                    className="flex h-[60px] w-[60px] flex-col items-center justify-center rounded-md outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-60"
                >
                    {selectedIcon ? (
                        <>
                            {SelectedIconComponent && (
                                <SelectedIconComponent size={24} style={{ color: iconColor }} />
                            )}
                            <span className="mt-1 max-w-[50px] truncate text-center text-[10px] leading-3 text-muted-foreground">
                                {selectedIcon}
                            </span>
                        </>
                    ) : (
                        <span className="mt-1 max-w-[50px] truncate text-center text-[10px] leading-3 text-muted-foreground">
                            {placeholder}
                        </span>
                    )}
                </button>
            </div>

            <Dialog
                open={isDialogOpen}
                onOpenChange={(open) => {
                    if (!open) {
                        setIsDialogOpen(false);
                        setSearchTerm('');
                    }
                }}
            >
                <DialogContent className="flex max-h-[90vh] flex-col sm:max-w-[800px]">
                    <DialogHeader>
                        <DialogTitle>{t('Select Icon')}</DialogTitle>
                    </DialogHeader>

                    <div className="flex min-h-0 flex-col gap-4">
                        <div className="flex min-w-0 flex-col gap-1.5">
                            <Label htmlFor={searchId}>{t('ui.search')}</Label>
                            <Input
                                id={searchId}
                                placeholder={t('Search icons...')}
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value || '')}
                                autoFocus
                            />
                        </div>

                        <p className="text-sm text-muted-foreground">
                            {t('Found {{count}} icons', { count: filteredIcons.length })}
                        </p>

                        <TooltipProvider>
                            <div className="grid max-h-[400px] grid-cols-[repeat(auto-fill,minmax(60px,1fr))] gap-2 overflow-y-auto p-2">
                                {filteredIcons.map((iconName) => {
                                    const IconComponent = fluentIconComponent(iconName);
                                    const isSelected = selectedIcon === iconName;
                                    return (
                                        <Tooltip key={iconName}>
                                            <TooltipTrigger asChild>
                                                <button
                                                    type="button"
                                                    onClick={() => handleIconSelect(iconName)}
                                                    className={cn(
                                                        "flex h-[60px] min-w-0 flex-col items-center justify-center rounded-md border transition-colors",
                                                        isSelected
                                                            ? "border-primary bg-accent"
                                                            : "border-input bg-background hover:bg-accent"
                                                    )}
                                                >
                                                    {IconComponent && <IconComponent size={24} />}
                                                    <span className="mt-1 max-w-[50px] truncate text-center text-[10px] leading-3 text-muted-foreground">
                                                        {iconName}
                                                    </span>
                                                </button>
                                            </TooltipTrigger>
                                            <TooltipContent>{iconName}</TooltipContent>
                                        </Tooltip>
                                    );
                                })}
                            </div>
                        </TooltipProvider>
                    </div>
                </DialogContent>
            </Dialog>
        </>
    );
};